import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, MessageCircle, Mail, MapPin, Loader2, LifeBuoy } from "lucide-react";
import { Chapter, Reveal } from "@/components/Chapter";
import { useContent } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-400/30 transition-colors";

export const Contact = () => {
  const { SITE, ENQUIRY_TOPICS } = useContent();
  const [form, setForm] = useState({ name: "", email: "", phone: "", package: "Something else", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Message sent — our team will reply within one working day.");
      setForm({ name: "", email: "", phone: "", package: "Something else", message: "" });
    } catch (err) {
      toast.error("Something went wrong sending your message. Please try WhatsApp or email instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 border-t border-slate-200 bg-white overflow-hidden" data-testid="contact-section">
      <div className="glow-amber-light absolute top-0 right-0 h-96 w-96 rounded-full" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
        <Chapter
          id="08"
          name="Contact"
          title="Tell us what you're building. Our team replies within one working day."
        />

        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12">
          <Reveal>
            <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-slate-50/60 p-8 md:p-10 space-y-5 shadow-sm" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Name *</label>
                  <input id="contact-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Email *</label>
                  <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-phone" className="mb-2 block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Phone / WhatsApp</label>
                  <input id="contact-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-package" className="mb-2 block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">What do you need?</label>
                  <select id="contact-package" data-testid="contact-package-select" value={form.package} onChange={set("package")} className={inputCls}>
                    {ENQUIRY_TOPICS.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="contact-message" className="mb-2 block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">About your project *</label>
                <textarea id="contact-message" data-testid="contact-message-input" required minLength={10} rows={5} value={form.message} onChange={set("message")} placeholder="What does your business do, and what should this project achieve?" className={`${inputCls} resize-none`} />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="inline-flex items-center gap-2 rounded-full bg-[#0B1528] px-8 py-4 text-base font-semibold text-white hover:bg-amber-500 hover:text-[#0B1528] active:scale-95 disabled:opacity-60 transition-all duration-300"
              >
                {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="space-y-5">
              <p className="text-sm text-slate-500 leading-relaxed">Prefer to talk directly? Every enquiry is handled by a dedicated team member — you'll always know exactly who is working on your project.</p>
              <a
                href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(SITE.whatsappMessage)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:border-emerald-400 hover:-translate-y-1 transition-all duration-300 group"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 border border-emerald-200">
                  <MessageCircle className="h-5 w-5 text-emerald-600" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-slate-800 group-hover:text-emerald-600 transition-colors">WhatsApp us</span>
                  <span className="block text-xs text-slate-500 mt-0.5">Fastest reply — usually within hours</span>
                </span>
              </a>
              <a
                href={`mailto:${SITE.email}?subject=Project%20enquiry%20—%20Desantrix`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:border-amber-400 hover:-translate-y-1 transition-all duration-300 group"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50 border border-amber-200">
                  <Mail className="h-5 w-5 text-amber-600" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-slate-800 group-hover:text-amber-600 transition-colors">{SITE.email}</span>
                  <span className="block text-xs text-slate-500 mt-0.5">New projects &amp; enquiries</span>
                </span>
              </a>
              <a
                href={`mailto:${SITE.supportEmail}?subject=Support%20request%20—%20Desantrix`}
                data-testid="contact-support-link"
                className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:border-blue-400 hover:-translate-y-1 transition-all duration-300 group"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 border border-blue-200">
                  <LifeBuoy className="h-5 w-5 text-blue-600" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">{SITE.supportEmail}</span>
                  <span className="block text-xs text-slate-500 mt-0.5">Existing clients — facing a problem? We're on it</span>
                </span>
              </a>
              <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="contact-location">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 border border-slate-200">
                  <MapPin className="h-5 w-5 text-slate-600" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-slate-800">{SITE.location}</span>
                  <span className="block text-xs text-slate-500 mt-0.5">Serving clients worldwide, remotely</span>
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
